import React, { useState, useEffect, useRef } from 'react';
import './CoachingCenter.css';

const CoachingCenter = () => {
  const [activeTab, setActiveTab] = useState('all');
  const gridRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const cards = gridRef.current?.querySelectorAll('.teacher-card');
    cards?.forEach((c) => observer.observe(c));
    return () => cards?.forEach((c) => observer.unobserve(c));
  }, [activeTab]);

  const programs = [
    {
      id: 'mcat',
      icon: '\u{1FA7A}',
      title: 'M-CAT Preparation',
      tagline: 'Medical College Admission Test',
      description: 'Comprehensive preparation for MDCAT with expert faculty covering Biology, Chemistry, Physics & English.',
      subjects: ['Biology', 'Chemistry', 'Physics', 'English'],
      color: '#0e7c61',
    },
    {
      id: 'ecat',
      icon: '\u2699\uFE0F',
      title: 'E-CAT Preparation',
      tagline: 'Engineering College Admission Test',
      description: 'Expert coaching for ECAT covering Mathematics, Physics, Chemistry & English for top engineering universities.',
      subjects: ['Mathematics', 'Physics', 'Chemistry', 'English'],
      color: '#1e40af',
    },
    {
      id: 'ics',
      icon: '\uD83D\uDCBB',
      title: 'ICS Program',
      tagline: 'Intermediate in Computer Science',
      description: 'Complete ICS coaching with dedicated Computer Science, Mathematics, Physics & English faculty.',
      subjects: ['Computer', 'Mathematics', 'Physics', 'English'],
      color: '#7c3aed',
    },
  ];

  const teachers = [
    { name: 'Sir Mahaveer Lohia', subject: 'Mathematics', icon: '\uD83D\uDCd0', programs: ['ecat', 'ics'] },
    { name: 'Sir Raja Ramesh', subject: 'Physics', icon: '\u269B\uFE0F', programs: ['mcat', 'ecat', 'ics'] },
    { name: 'Sir Farhad Malik', subject: 'Physics', icon: '\u269B\uFE0F', programs: ['mcat', 'ecat', 'ics'] },
    { name: 'Sir Om Parkash', subject: 'Chemistry', icon: '\uD83E\uDDEA', programs: ['mcat', 'ecat'] },
    { name: 'Sir Noman Qureshi', subject: 'Chemistry', icon: '\uD83E\uDDEA', programs: ['mcat', 'ecat'] },
    { name: 'Sir Ahsan', subject: 'Chemistry', icon: '\uD83E\uDDEA', programs: ['mcat', 'ecat'] },
    { name: 'Sir Sunil Narwani', subject: 'Biology', icon: '\uD83E\uDDEC', programs: ['mcat'] },
    { name: 'Sir Salman Hussain', subject: 'Biology', icon: '\uD83E\uDDEC', programs: ['mcat'] },
    { name: 'Sir Bakhtiar Malik', subject: 'Biology', icon: '\uD83E\uDDEC', programs: ['mcat'] },
    { name: 'Sir Khaliq-u-Zaman', subject: 'English', icon: '\uD83D\uDCD6', programs: ['mcat', 'ecat', 'ics'] },
    { name: 'Sir Aqif', subject: 'English', icon: '\uD83D\uDCD6', programs: ['mcat', 'ecat', 'ics'] },
    { name: 'Sir Mahaveer Kumar', subject: 'Computer', icon: '\uD83D\uDDA5\uFE0F', programs: ['ics'] },
    { name: 'Sir Aijaz Lagharia', subject: 'Computer', icon: '\uD83D\uDDA5\uFE0F', programs: ['ics'] },
  ];

  const subjectColors = {
    Mathematics: '#d97706',
    Physics: '#0284c7',
    Chemistry: '#059669',
    Biology: '#dc2626',
    English: '#7c3aed',
    Computer: '#0891b2',
  };

  const filteredTeachers = activeTab === 'all'
    ? teachers
    : teachers.filter((t) => t.programs.includes(activeTab));

  return (
    <section id="coaching" className="coaching-center">
      <div className="container">
        <div className="section-title">
          <h2>MTC Coaching Center</h2>
          <p>Expert faculty for M-CAT, E-CAT & ICS preparation to help you secure admission in top universities</p>
        </div>

        {/* Program Cards */}
        <div className="coaching-programs">
          {programs.map((program) => (
            <div className="coaching-program-card" key={program.id} style={{ '--program-color': program.color }}>
              <div className="program-badge">{program.icon}</div>
              <h3>{program.title}</h3>
              <span className="program-tagline">{program.tagline}</span>
              <p>{program.description}</p>
              <div className="program-subjects">
                {program.subjects.map((s) => (
                  <span className="subject-chip" key={s} style={{ '--chip-color': subjectColors[s] }}>{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="coaching-tabs">
          <button className={`coaching-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
            All Faculty
          </button>
          <button className={`coaching-tab ${activeTab === 'mcat' ? 'active' : ''}`} onClick={() => setActiveTab('mcat')} style={{ '--tab-color': '#0e7c61' }}>
            M-CAT
          </button>
          <button className={`coaching-tab ${activeTab === 'ecat' ? 'active' : ''}`} onClick={() => setActiveTab('ecat')} style={{ '--tab-color': '#1e40af' }}>
            E-CAT
          </button>
          <button className={`coaching-tab ${activeTab === 'ics' ? 'active' : ''}`} onClick={() => setActiveTab('ics')} style={{ '--tab-color': '#7c3aed' }}>
            ICS
          </button>
        </div>

        {/* Faculty Grid */}
        <div className="teachers-grid" ref={gridRef} key={activeTab}>
          {filteredTeachers.map((teacher, index) => (
            <div
              className="teacher-card"
              key={teacher.name}
              style={{
                transitionDelay: `${index * 0.06}s`,
                '--subject-color': subjectColors[teacher.subject],
              }}
            >
              <div className="teacher-avatar">
                <span>{teacher.icon}</span>
              </div>
              <div className="teacher-info">
                <h4>{teacher.name}</h4>
                <span className="teacher-subject">{teacher.subject}</span>
              </div>
              <div className="teacher-programs">
                {teacher.programs.map((p) => (
                  <span className={`program-tag program-tag--${p}`} key={p}>
                    {p.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="coaching-cta">
          <div className="cta-content">
            <h3>Start Your Preparation Today</h3>
            <p>Join MTC Coaching Center and get expert guidance to crack M-CAT, E-CAT & ICS with flying colors!</p>
          </div>
          <a href="#enroll" className="btn btn-gold">Enroll Now</a>
        </div>
      </div>
    </section>
  );
};

export default CoachingCenter;
